import type {
  DocumentSchema,
  SearchResponse,
  SearchResponseHit,
} from 'typesense/lib/Typesense/Documents';
import type { SetlistContext } from './client';
import { STOCK_VIDEO_COLLECTION } from './collections';
import { getCategorySearchDefaults } from './get-category-query-defaults';
import { getStockFootageByCategoryEmbedding } from './get-stock-footage-by-category';
import { getVideoCategory } from './get-video-category';
import { extractHits } from './group-limit';
import { intersperseAssets } from './intersperse-assets';
import { getMotionGraphicsCategory } from './lookups';
import { LUTS_FILTER } from './lut-filters';
import { MOTION_GRAPHICS_FILTER } from './motion-graphics-filter';
import { getSearches } from './motion-graphics-queries';
import {
  categoryFiltersToFilterBy,
  getCategoryPreselectedFilters,
  getLutCategoryPreselectedFilters,
} from './parse-category-query';
import { getMusicSearches, getSfxSearches, shouldIntersperse } from './queries';
import { fetchSearchResults } from './search-assets';
import { getSfxSimilaritySearches } from './sfx-similarity-queries';
import { getSimilaritySearches } from './similarity-queries';
import type {
  DropTokensMode,
  SfxSimilarityField,
  SfxSimilarityWeights,
  SimilarityField,
  SimilarityWeights,
} from './similarity-weights';
import { getMusicPageFilter } from './track-engagement-join';
import type {
  SfxWithEngagementDocument,
  TracksWithEngagementDocument,
} from './tracks-with-engagement';
import type { SortOption } from './typesense-sort';
import { getStockVideoSortBy } from './typesense-sort';

export type AssetLibrary =
  | 'music'
  | 'sfx'
  | 'motion-graphics'
  | 'luts'
  | 'stock-video';

export interface AssetQuery {
  // Extra `filter_by` on top of whatever the page or asset type implies.
  filterBy?: string;
  // Mix free and premium results. Only ever applies to non-subscribers on the
  // default sort; ignored otherwise.
  intersperse?: boolean;
  // Music only: rank alternative versions first.
  isAlternativeFirst?: boolean;
  isSubscribed?: boolean;
  page?: number;
  perPage?: number;
  q?: string;
  sort?: SortOption;
  // SFX only: which relevance score to rank by.
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  type: AssetLibrary;
}

export interface PageAssetQuery extends AssetQuery {
  /**
   * Music category pages only, and required for them: the page's rule as
   * compiled by the API. Music collection pages derive theirs from `slug`.
   */
  pageFilter?: string | null;
  // The page's full site path, e.g. `/music/category/jazz`.
  slug: string;
}

export interface AssetListing<T extends DocumentSchema = DocumentSchema> {
  found: number;
  // What the page shows, in order.
  hits: SearchResponseHit<T>[];
  page: number;
  // The raw result sets: [premium, free] when interspersed, otherwise one.
  results: SearchResponse<T>[];
}

const STOCK_VIDEO_PAGE_SIZE = 24;
const STOCK_VIDEO_CATEGORY_PREFIX = '/stock-video/category/';

const joinFilters = (...filters: (string | false | null | undefined)[]) =>
  filters.filter(Boolean).join(' && ');

const runSearches = async <T extends DocumentSchema>(
  { client }: SetlistContext,
  searches: object[],
  useCache = true
): Promise<SearchResponse<T>[]> => {
  const response = await client.multiSearch.perform<T[]>(
    { searches } as Parameters<typeof client.multiSearch.perform>[0],
    { use_cache: useCache }
  );

  if (!(response && Array.isArray(response.results))) {
    throw new Error('Invalid search response structure');
  }

  return response.results as SearchResponse<T>[];
};

const toListing = <T extends DocumentSchema>(
  results: SearchResponse<T>[],
  page: number,
  interspersed: boolean
): AssetListing<T> => ({
  results,
  page,
  found: results.reduce((sum, result) => sum + (result.found ?? 0), 0),
  hits:
    interspersed && results.length === 2 && results[0] && results[1]
      ? intersperseAssets({
          premiumAssetSearchResults: results[0],
          freeAssetSearchResults: results[1],
        })
      : extractHits(results[0] ?? ({} as SearchResponse<T>)),
});

const requireMusicPageFilter = ({
  slug,
  pageFilter,
}: {
  slug: string;
  pageFilter?: string | null;
}) => {
  const filter = getMusicPageFilter(pageFilter, slug);

  if (!filter) {
    throw new Error(
      `Music category page "${slug}" needs the pageFilter the API compiles for it`
    );
  }

  return filter;
};

const listTracks = async (
  context: SetlistContext,
  {
    type,
    q,
    filterBy = '',
    sort = 'relevancy',
    isSubscribed = false,
    intersperse = true,
    page = 1,
    perPage,
    isAlternativeFirst,
    sortScoreVersion = 'v1',
  }: AssetQuery,
  pageContext?: { slug: string; pageFilter?: string | null }
) => {
  const fetchFreeAndPremium = shouldIntersperse({
    sort,
    isSubscribed,
    requested: intersperse,
  });
  const shared = {
    q,
    filter_by: filterBy,
    sort,
    fetchFreeAndPremium,
    page,
    contextSlug: pageContext?.slug,
    sortScoreVersion,
    isSubscribedUser: isSubscribed,
    perPage,
  };

  const searches =
    type === 'music'
      ? getMusicSearches({
          ...shared,
          contextFilter: pageContext
            ? requireMusicPageFilter(pageContext)
            : undefined,
          isAlternativeFirst,
        })
      : getSfxSearches(shared);

  return toListing(
    await runSearches(context, searches),
    page,
    fetchFreeAndPremium
  );
};

const listMotionGraphics = async (
  context: SetlistContext,
  {
    type,
    q,
    filterBy,
    sort = 'relevancy',
    isSubscribed = false,
    intersperse = true,
    page = 1,
    perPage,
  }: AssetQuery,
  categoryFilter?: string
) => {
  const fetchFreeAndPremium = intersperse && !isSubscribed;
  const searches = getSearches({
    q,
    filter_by: joinFilters(
      categoryFilter,
      filterBy,
      type === 'luts' ? LUTS_FILTER : MOTION_GRAPHICS_FILTER
    ),
    sort,
    fetchFreeAndPremium,
    page,
    perPage,
  });

  return toListing(
    await runSearches(context, searches),
    page,
    fetchFreeAndPremium
  );
};

const getMotionGraphicsPageFilter = async (
  context: SetlistContext,
  type: AssetLibrary,
  slug: string
) => {
  const category = await getMotionGraphicsCategory(context, slug);

  if (!category) {
    throw new Error(`No category at "${slug}"`);
  }

  return categoryFiltersToFilterBy(
    type === 'luts'
      ? getLutCategoryPreselectedFilters(category, context.onError)
      : getCategoryPreselectedFilters(category, context.onError)
  );
};

const listStockVideoPage = async (
  context: SetlistContext,
  {
    slug,
    filterBy,
    sort = 'relevancy',
    isSubscribed = false,
    page = 1,
    perPage = STOCK_VIDEO_PAGE_SIZE,
  }: PageAssetQuery
) => {
  const category = await getVideoCategory(
    context,
    slug.replace(STOCK_VIDEO_CATEGORY_PREFIX, '')
  );

  if (!category) {
    throw new Error(`No category at "${slug}"`);
  }

  const { filterBy: categoryFilter, ...search } =
    getCategorySearchDefaults(category);

  const response = await getStockFootageByCategoryEmbedding(context, {
    categoryEmbedding: search.categoryEmbedding,
    distanceThreshold: search.distanceThreshold,
    unionKeywords: search.unionKeywords,
    page,
    perPage,
    filterBy: joinFilters(categoryFilter, filterBy) || undefined,
    isSubscribedUser: isSubscribed,
    sortBy: getStockVideoSortBy({ sortOption: sort }),
    slug,
  });

  return toListing([response], page, false);
};

/**
 * The assets a category or collection page shows, in the order it shows them.
 *
 * @example
 * setlist.getAssetsForPage({
 *   type: 'music',
 *   slug: '/music/category/jazz',
 *   pageFilter: page.filter,
 * });
 */
export const getAssetsForPage = async (
  context: SetlistContext,
  query: PageAssetQuery
): Promise<AssetListing> => {
  switch (query.type) {
    case 'music':
    case 'sfx':
      return listTracks(context, query, query);
    case 'motion-graphics':
    case 'luts':
      return listMotionGraphics(
        context,
        query,
        await getMotionGraphicsPageFilter(context, query.type, query.slug)
      );
    default:
      return listStockVideoPage(context, query);
  }
};

/** Assets matching a search term and filters, outside any page. */
export const searchAssets = async (
  context: SetlistContext,
  query: AssetQuery
): Promise<AssetListing> => {
  switch (query.type) {
    case 'music':
    case 'sfx':
      return listTracks(context, query);
    case 'motion-graphics':
    case 'luts':
      return listMotionGraphics(context, query);
    default: {
      const page = query.page ?? 1;
      const response = await fetchSearchResults(context, {
        searchTerm: query.q ?? '',
        pageNumber: page,
        type: STOCK_VIDEO_COLLECTION,
        filter_by: query.filterBy,
        isSubscribedUser: query.isSubscribed,
        perPage: query.perPage,
      });

      return toListing(response ? [response] : [], page, false);
    }
  }
};

interface SimilarAssetsOptions {
  // Leave the reference itself out of the results.
  excludeId?: string;
  isSubscribed?: boolean;
  page?: number;
  sort?: SortOption;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  // Off while tuning weights, so a change shows up straight away.
  useCache?: boolean;
}

// Tuning knobs for the admin debug panel. Leave unset for the live ranking.
interface SimilarityOverrides<Weights, Field extends string> {
  dropTokensMode?: DropTokensMode | null;
  fieldOrder?: Field[] | null;
  fieldsEnabled?: Record<Field, boolean> | null;
  weights?: Weights | null;
}

export type SimilarAssetsQuery = SimilarAssetsOptions &
  (
    | {
        type: 'music';
        reference: TracksWithEngagementDocument;
        overrides?: SimilarityOverrides<SimilarityWeights, SimilarityField>;
      }
    | {
        type: 'sfx';
        reference: SfxWithEngagementDocument;
        // Mix free and premium results. Defaults to on for non-subscribers.
        intersperse?: boolean;
        overrides?: SimilarityOverrides<
          SfxSimilarityWeights,
          SfxSimilarityField
        >;
      }
  );

/**
 * Assets similar to a reference one. The reference is a document rather than
 * an id so an asset can be checked before it is indexed.
 */
export const getSimilarAssets = async (
  context: SetlistContext,
  query: SimilarAssetsQuery
): Promise<AssetListing> => {
  const {
    isSubscribed = false,
    page = 1,
    sort = 'relevancy',
    sortScoreVersion = 'v1',
    excludeId,
    useCache = true,
  } = query;
  const shared = {
    sort,
    page,
    sortScoreVersion,
    isSubscribedUser: isSubscribed,
    dropTokensModeOverride: query.overrides?.dropTokensMode,
  };

  if (query.type === 'music') {
    const searches = getSimilaritySearches({
      ...shared,
      referenceTrack: query.reference,
      excludeTrackId: excludeId,
      weightOverrides: query.overrides?.weights,
      fieldOrderOverride: query.overrides?.fieldOrder,
      fieldsEnabledOverride: query.overrides?.fieldsEnabled,
    });

    return toListing(
      await runSearches(context, searches, useCache),
      page,
      false
    );
  }

  const fetchFreeAndPremium = query.intersperse ?? !isSubscribed;
  const searches = getSfxSimilaritySearches({
    ...shared,
    referenceSfx: query.reference,
    excludeSfxId: excludeId,
    fetchFreeAndPremium,
    weightOverrides: query.overrides?.weights,
    fieldOrderOverride: query.overrides?.fieldOrder,
    fieldsEnabledOverride: query.overrides?.fieldsEnabled,
  });

  return toListing(
    await runSearches(context, searches, useCache),
    page,
    fetchFreeAndPremium
  );
};
